import React from 'react';
import { 
  Calendar, 
  Users, 
  Briefcase, 
  FileText, 
  UserCircle, 
  Settings,
  ArrowRight,
  Clock,
  CheckCircle2
} from 'lucide-react';

interface SearchResult {
  area: string;
  path?: string;
  fullPath?: string;
  url?: string;
  description?: string;
  count?: number;
}

interface SearchResultCardProps {
  results: SearchResult[];
  query?: string;
  onResultClick?: (result: SearchResult) => void;
  isDark?: boolean;
}

// Icon mapping for different areas
const areaIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  'Timesheets': Calendar,
  'Staff': Users,
  'Projects': Briefcase,
  'Contacts': UserCircle,
  'Admin': Settings,
  'Leave': Clock,
  'Tasks': CheckCircle2,
  'default': FileText
};

// Color mapping for different areas - using standard app color scheme
const areaColors: Record<string, { light: string; dark: string; icon: string }> = {
  'Timesheets': {
    light: 'bg-white border-gray-200 text-gray-900',
    dark: 'bg-gray-700 border-gray-600 text-gray-300',
    icon: 'text-accent-500'
  },
  'Staff': {
    light: 'bg-white border-gray-200 text-gray-900',
    dark: 'bg-gray-700 border-gray-600 text-gray-300',
    icon: 'text-purple-500'
  },
  'Projects': {
    light: 'bg-white border-gray-200 text-gray-900',
    dark: 'bg-gray-700 border-gray-600 text-gray-300',
    icon: 'text-success-500'
  },
  'Contacts': {
    light: 'bg-white border-gray-200 text-gray-900',
    dark: 'bg-gray-700 border-gray-600 text-gray-300',
    icon: 'text-primary-500'
  },
  'Admin': {
    light: 'bg-white border-gray-200 text-gray-900',
    dark: 'bg-gray-700 border-gray-600 text-gray-300',
    icon: 'text-gray-500'
  },
  'Leave': {
    light: 'bg-white border-gray-200 text-gray-900',
    dark: 'bg-gray-700 border-gray-600 text-gray-300',
    icon: 'text-warning-500'
  },
  'Tasks': {
    light: 'bg-white border-gray-200 text-gray-900',
    dark: 'bg-gray-700 border-gray-600 text-gray-300',
    icon: 'text-indigo-500'
  }
};

export const SearchResultCard: React.FC<SearchResultCardProps> = ({
  results,
  query,
  onResultClick,
  isDark = false
}) => {
  if (!results || results.length === 0) {
    return (
      <div className={`rounded-lg border p-4 ${
        isDark 
          ? 'bg-gray-700 border-gray-600 text-gray-400' 
          : 'bg-white border-gray-200 text-gray-600'
      }`}>
        <p className="text-sm">No results found</p>
      </div>
    );
  }

  const handleClick = (result: SearchResult) => {
    if (onResultClick) {
      onResultClick(result);
    } else if (result.url) {
      // Default behavior: navigate to URL
      window.location.href = result.url;
    }
  };

  return (
    <div className="space-y-2">
      {results.map((result, index) => {
        const area = result.area || 'default';
        const IconComponent = areaIcons[area] || areaIcons['default'];
        const colors = areaColors[area] || areaColors['default'] || {
          light: 'bg-white border-gray-200 text-gray-900',
          dark: 'bg-gray-700 border-gray-600 text-gray-300',
          icon: 'text-gray-500'
        };

        const cardClasses = `rounded-lg border p-4 cursor-pointer transition-all hover:shadow-md ${
          isDark 
            ? `${colors.dark} hover:border-opacity-70` 
            : `${colors.light} hover:border-opacity-70`
        }`;

        return (
          <div
            key={index}
            onClick={() => handleClick(result)}
            className={cardClasses}
          >
            <div className="flex items-start gap-3">
              {/* Icon */}
              <div className={`flex-shrink-0 mt-0.5 ${colors.icon}`}>
                <IconComponent className="w-5 h-5" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                {/* Title */}
                <div className="flex items-center justify-between gap-2 mb-1">
                  <h3 className={`font-semibold text-sm ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {result.fullPath || result.path || result.area}
                  </h3>
                  {result.count !== undefined && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      isDark 
                        ? 'bg-accent-900/50 text-accent-300' 
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {result.count} {result.count === 1 ? 'item' : 'items'}
                    </span>
                  )}
                </div>

                {/* Description */}
                {result.description && (
                  <p className={`text-xs mb-2 ${
                    isDark ? 'text-gray-400' : 'text-gray-700'
                  }`}>
                    {result.description}
                  </p>
                )}

                {/* Path breadcrumb */}
                {result.path && result.path !== result.area && (
                  <div className={`text-xs flex items-center gap-1 ${
                    isDark ? 'text-gray-500' : 'text-gray-600'
                  }`}>
                    <span>{result.area}</span>
                    <span>/</span>
                    <span>{result.path}</span>
                  </div>
                )}
              </div>

              {/* Arrow */}
              <div className={`flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
